import {
  DataTableSource,
  type DataTableSortState,
  type DataTableSourceOptions,
  type FilterSelection,
} from "@carbon6/c6-frontend-design";
import type { DatasetSort, DatasetSortOrder } from "@/api";
import type { DatasetStorageBackend } from "@/api/datasetStorageBackend";
import {
  createLabelFilterQuery,
  isFilterSelectionEqual,
  normalizeFilterSelection,
  type DatasetMatchMode,
  type LabelFilterQuery,
} from "@/components/filter";
import { sortSignature } from "@/components/table/sortSignature";
import { normalizeWhitespace } from "@/utils";
import type { DatasetTableRow } from "./DatasetTableRow";

// 표 헤더 컬럼 키 → 서버 정렬 기준. 서버가 정렬을 지원하지 않는 열(라벨·담당자·메모·정체성 facet)은
// 여기 없다. 컬럼 정의도 그 열을 정렬 불가로 둔다. 매핑이 없으면 정렬 없이 요청하므로 표시 순서와
// 헤더 표식이 어긋나기 때문이다.
const SORT_AXIS_BY_COLUMN: Readonly<Record<string, DatasetSort>> = {
  display_name: "display_name",
  created_at: "created_at",
  total_episodes: "episode_count",
  median_episode_length_sec: "episode_length",
  total_duration_sec: "total_duration",
  total_size_bytes: "total_size",
};

/**
 * Dataset 행 목록 소스의 공통 criteria 축(검색어, 필터 selection, 일치 방식) 베이스.
 *
 * Owns:
 * - criteria 정규화·동등 비교·저장. "같은 조건이면 재요청하지 않음" 의 단일 기준이다.
 * - criteria → 서버 쿼리 조각 변환(`criteriaQuery`)과 prefetch 캐시 서명.
 * - 탭 전환 시 조건을 맞추는 일괄 적용(`applyCriteria`). 두 축의 변경을 reload 1회로 합친다.
 *
 * - 정렬 기준 통지와 서버 쿼리 변환. 세 탭(데이터셋·보관됨·북마크)이 같은 컬럼을 쓰므로
 *   컬럼 → 정렬 기준 매핑도 한 곳에 둔다.
 *
 * Does not own:
 * - 엔드포인트 선택·행 조립·인스턴스 고정 축(status/reviewed). 각 서브클래스가 정한다.
 */
export abstract class DatasetCriteriaTableSource extends DataTableSource<DatasetTableRow> {
  private searchQuery = "";
  private filterSelection: FilterSelection = {};
  // 기본은 AND(all)다. 컨트롤러(DatasetFilterController)의 기본과 반드시 같아야
  // commitCriteria 동등 비교가 첫 커밋을 재조회 대상으로 잘못 판정하지 않는다.
  private matchMode: DatasetMatchMode = "all";
  // 폴더 축은 다른 조건과 소유자가 다르다. URL(`?folder=`)이 소유하고, 서버에서도
  // `filter_match` 밖의 고정 게이트라 일치 방식과 함께 커밋되지 않는다.
  private folderToken: string | null = null;
  // 저장 위치 게이트. 폴더처럼 URL(`?storage_backend=`)이 소유하고 서버에서도 `filter_match` 밖의
  // 고정 게이트다. 폴더와 달리 세 탭(데이터셋·보관됨·북마크)의 소스가 모두 받는다.
  private storageBackend: DatasetStorageBackend | null = null;

  // 목록 API 는 page 요청마다 전체 수를 다시 센다. 그래서 조건을 바꿔 받은 page 뒤에는 다음 page 를
  // 미리 받지 않는다. 같은 조건에서 page 를 넘길 때만 넘긴 방향의 다음 page 를 받는다.
  protected constructor(options: DataTableSourceOptions<DatasetTableRow>) {
    super({ ...options, prefetchMode: "paging" });
  }

  // 제목 옆 전환기에서 고른 저장 위치. 값이 같으면 재조회하지 않는다.
  setStorageBackend(backend: DatasetStorageBackend | null): Promise<void> {
    if (backend === this.storageBackend) {
      return Promise.resolve();
    }
    this.storageBackend = backend;
    return this.goToPage(1);
  }

  // 재조회 없이 값만 설정한다. 첫 로드 전과 비활성 탭의 소스에 쓴다. 비활성 소스는 탭이 활성화될 때
  // 이 값으로 다시 받는다.
  seedStorageBackend(backend: DatasetStorageBackend | null): void {
    this.storageBackend = backend;
  }

  // 트리에서 고른 폴더. 값이 같으면 재조회하지 않는다. 트리를 다시 그릴 때마다 같은
  // 선택이 다시 전달되기 때문이다.
  setFolderToken(token: string | null): Promise<void> {
    if (token === this.folderToken) {
      return Promise.resolve();
    }
    this.folderToken = token;
    return this.goToPage(1);
  }

  // 탭 전환·재마운트 때 재조회 없이 현재 폴더를 설정한다. 목록 첫 로드가 이미 이 값을
  // 포함해 요청하므로, 여기서 goToPage 를 호출하면 첫 페이지를 두 번 받는다.
  seedFolderToken(token: string | null): void {
    this.folderToken = token;
  }

  setSearchQuery(searchQuery: string): Promise<void> {
    return (
      this.commitCriteria(searchQuery, this.filterSelection, this.matchMode) ?? Promise.resolve()
    );
  }

  setFilterSelection(selection: FilterSelection, mode: DatasetMatchMode): Promise<void> {
    return this.commitCriteria(this.searchQuery, selection, mode) ?? Promise.resolve();
  }

  // 탭 전환처럼 새 활성 소스가 공유 필터의 최종 조건을 따라잡을 때 쓴다. 재조회를 일으켰으면
  // true 를 반환한다. 호출자는 이 값으로 "조건이 같아도 재조회" 정책의 강제 refresh 를 중복하지 않는다.
  // 이 소스가 비활성인 동안 다른 탭이 목록을 바꿀 수 있다. 그래서 전에 캐시한 조건으로 돌아와도
  // page 캐시를 쓰지 않는다.
  applyCriteria(query: string, selection: FilterSelection, mode: DatasetMatchMode): boolean {
    return this.commitCriteria(query, selection, mode, () => this.refresh(1)) !== null;
  }

  // criteria 비교·저장·reload 의 단일 경로. 조건이 같으면 null(요청 없음), 바뀌면 page 1 을 reload 한다.
  private commitCriteria(
    query: string,
    selection: FilterSelection,
    mode: DatasetMatchMode,
    reload: () => Promise<void> = () => this.goToPage(1),
  ): Promise<void> | null {
    const nextQuery = normalizeWhitespace(query);
    const nextSelection = normalizeFilterSelection(selection);
    if (
      nextQuery === this.searchQuery &&
      isFilterSelectionEqual(this.filterSelection, nextSelection) &&
      mode === this.matchMode
    ) {
      return null;
    }

    this.searchQuery = nextQuery;
    this.filterSelection = nextSelection;
    this.matchMode = mode;
    return reload();
  }

  // 정렬은 서버가 전체 행에 대해 한다. 로드된 페이지만 클라이언트에서 정렬하면 페이지 2 에서
  // "전체 정렬" 처럼 보이는 잘못된 순서가 나온다.
  override get sortsOnServer(): boolean {
    return true;
  }

  // 정렬이 바뀌면 첫 페이지부터 다시 받는다. DataTable 은 마운트 시에도 활성 정렬을
  // 통지한다(초기값 null). 그래서 같은 정렬이면 재요청하지 않아야 첫 페이지를 두 번 받지 않는다.
  override setSort(sort: DataTableSortState | null): void {
    const changed = sortSignature(sort) !== sortSignature(this.sortState);
    super.setSort(sort);
    if (changed) {
      void this.goToPage(1);
    }
  }

  protected override cacheSignature(): string | null {
    // search·filter·mode·정렬이 바뀌면 서명이 달라져 prefetch 캐시가 무효가 된다.
    // 서브클래스의 인스턴스 고정 축(status·reviewed)은 서명에 넣지 않는다.
    return JSON.stringify([
      this.searchQuery,
      this.filterSelection,
      this.matchMode,
      this.folderToken,
      this.storageBackend,
      sortSignature(this.sortState),
    ]);
  }

  // 서브클래스 onPage 가 자기 목록 API 호출에 합치는 criteria 쿼리 조각.
  protected criteriaQuery(): {
    search?: string;
    folder?: string;
    storage_backend?: DatasetStorageBackend;
  } & LabelFilterQuery {
    return {
      ...(this.searchQuery ? { search: this.searchQuery } : {}),
      ...(this.folderToken === null ? {} : { folder: this.folderToken }),
      ...(this.storageBackend === null ? {} : { storage_backend: this.storageBackend }),
      ...createLabelFilterQuery(this.filterSelection, this.matchMode),
    };
  }

  // 서브클래스 onPage 가 합치는 정렬 쿼리 조각. 정렬이 없거나 서버가 정렬을 지원하지 않는 열이면
  // 비어 있다. 그러면 서버 기본 순서(목록은 생성 최신순, 북마크는 담은 순)로 돌아온다.
  protected sortQuery(): { sort?: DatasetSort; order?: DatasetSortOrder } {
    const sort = this.sortState;
    if (sort === null) {
      return {};
    }
    const axis = SORT_AXIS_BY_COLUMN[sort.columnKey];
    return axis === undefined ? {} : { sort: axis, order: sort.direction };
  }
}
